<template>
  <div class="card card-body shadow-sm mt-0">
    <div class="form-header d-flex justify-content-between align-items-center">
      <h4 class="form-title">
        {{ route.params.id ? 'Editar Forma de Pagamento' : 'Nova Forma de Pagamento' }}
      </h4>
      <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
    </div>

    <BaseForm :isFormValid="isFormValid" :isSubmitting="isSubmitting" @submit="salvarForma" @cancel="voltar">
      <FormSection title="Informações da Forma">
        <!-- Linha com os selects para Operador e Meio -->
        <div class="d-flex gap-3 mb-3">
          <!-- Campo Operador -->
          <div class="flex-fill larger-field">
            <SelectField
              id="operador_id"
              v-model="forma.operador_id"
              :options="operadoresOptions"
              placeholder="Selecione o operador"
              label="Operador <span class='required'>*</span>"
              required
              @update:modelValue="onOperadorChange"
            />
            <div v-if="errors.operador_id" class="error-message">{{ errors.operador_id }}</div>
          </div>
          <!-- Campo Meio -->
          <div class="flex-fill larger-field">
            <SelectField
              id="meio_id"
              v-model="forma.meio_id"
              :options="meiosOptions"
              placeholder="Selecione o meio"
              label="Meio <span class='required'>*</span>"
              required
            />
            <div v-if="errors.meio_id" class="error-message">{{ errors.meio_id }}</div>
          </div>
        </div>

        <!-- Campo Forma de Pagamento -->
        <div class="d-flex gap-3 mb-2">
          <InputField
            id="forma"
            v-model="forma.forma"
            placeholder="Digite a forma de pagamento"
            label="Forma de Pagamento <span class='required'>*</span>"
            required
            :class="{ 'error-input': errors.forma }"
          />
          <div v-if="errors.forma" class="error-message">{{ errors.forma }}</div>
        </div>
      </FormSection>
    </BaseForm>
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import InputField from "@/components/Layout/InputField.vue";
import SelectField from "@/components/Layout/SelectField.vue";
import FormSection from "@/components/Layout/FormSection.vue";
import BaseForm from "@/components/Layout/BaseForm.vue";
import axios from "@/services/axios";

export default {
  components: { InputField, SelectField, FormSection, BaseForm },
  setup() {
    const route = useRoute();
    const router = useRouter();

    // Objeto reativo para a forma de pagamento (campo "ativo" removido)
    const forma = reactive({
      id: null,
      operador_id: '',
      meio_id: '',
      forma: "",
    });
    const errors = reactive({});
    const isSubmitting = ref(false);

    // Lista de operadores e opções para o SelectField
    const operadores = ref([]);
    const operadoresOptions = computed(() =>
      operadores.value.map(op => ({ text: op.operador, value: op.id }))
    );

    // Lista de meios – serão carregados conforme o operador selecionado
    const meios = ref([]);
    const meiosOptions = computed(() =>
      meios.value.map(m => ({ text: m.meio, value: m.id }))
    );

    // Busca os operadores a partir do endpoint
    const fetchOperadores = async () => {
      try {
        const response = await axios.get('/financeiro/parametros/operadores');
        operadores.value = response.data.data;
      } catch (error) {
        console.error("Erro ao carregar operadores:", error);
      }
    };

    // Busca os meios filtrando pelo operador selecionado
    const fetchMeios = async (operadorId) => {
      try {
        const response = await axios.get('/financeiro/parametros/meios', {
          params: { operador_id: operadorId }
        });
        meios.value = response.data.data;
      } catch (error) {
        console.error("Erro ao carregar meios:", error);
      }
    };

    // Quando o operador muda, limpa o meio selecionado e busca os meios relacionados
    const onOperadorChange = (newOperadorId) => {
      forma.meio_id = ''; // Resetar o campo meio
      if (newOperadorId) {
        fetchMeios(newOperadorId);
      } else {
        meios.value = [];
      }
    };

    // Validação do formulário
    const validateForm = () => {
      errors.operador_id = forma.operador_id ? "" : "Operador é obrigatório.";
      errors.meio_id = forma.meio_id ? "" : "Meio é obrigatório.";
      errors.forma = forma.forma ? "" : "A forma de pagamento é obrigatória.";
    };

    const isFormValid = computed(() => {
      return forma.operador_id && forma.meio_id && forma.forma.trim() !== "" &&
             !Object.values(errors).some(error => error);
    });

    const novoForma = () => {
      Object.assign(forma, {
        operador_id: '',
        meio_id: '',
        forma: "",
      });
      for (const key in errors) {
        if (Object.hasOwn(errors, key)) errors[key] = "";
      }
    };

    const salvarForma = async () => {
      if (isSubmitting.value) return;
      validateForm();
      if (!isFormValid.value) {
        console.error("O formulário contém erros:", errors);
        return;
      }
      isSubmitting.value = true;
      try {
        const method = forma.id ? "put" : "post";
        const url = forma.id
          ? `/financeiro/parametros/formas/${forma.id}`
          : "/financeiro/parametros/formas";
        await axios[method](url, {
          operador_id: forma.operador_id,
          meio_id: forma.meio_id,
          forma: forma.forma,
        });
        router.push("/financeiro/parametros/formas");
      } catch (error) {
        console.error("Erro ao salvar forma:", error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const voltar = () => {
      router.push("/financeiro/parametros/formas");
    };

    onMounted(async () => {
      await fetchOperadores();
      // Se estiver editando, carregar a forma e os meios relacionados ao operador selecionado
      const formaId = route.params.id;
      if (formaId) {
        try {
          const response = await axios.get(`/financeiro/parametros/formas/${formaId}`);
          Object.assign(forma, response.data);
          if (forma.operador_id) {
            await fetchMeios(forma.operador_id);
          }
        } catch (error) {
          console.error("Erro ao carregar forma:", error);
        }
      } else {
        novoForma();
      }
    });

    return {
      forma,
      errors,
      isFormValid,
      isSubmitting,
      salvarForma,
      voltar,
      route,
      operadoresOptions,
      meiosOptions,
      onOperadorChange,
    };
  },
};
</script>

<style scoped>
.card {
  max-width: 90%;
  margin: 0 auto;
}
.error-input {
  border-color: #dc3545;
}
.error-message {
  color: #dc3545;
  font-size: 0.875rem;
}
.larger-field {
  min-width: 180px;
}
</style>
